import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowLeft, ClipboardList, BookOpen, Sparkles, Users, Laptop, Building2, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }),
};

const FEATURES = [
  {
    icon: <ClipboardList className="h-6 w-6" />,
    titleKey: 'seminar.feature1_title',
    descKey: 'seminar.feature1_desc',
    color: 'text-emerald-500',
    bg: 'bg-emerald-50 border-emerald-200',
  },
  {
    icon: <BookOpen className="h-6 w-6" />,
    titleKey: 'seminar.feature2_title',
    descKey: 'seminar.feature2_desc',
    color: 'text-sky-500',
    bg: 'bg-sky-50 border-sky-200',
  },
  {
    icon: <Sparkles className="h-6 w-6" />,
    titleKey: 'seminar.feature3_title',
    descKey: 'seminar.feature3_desc',
    color: 'text-amber-500',
    bg: 'bg-amber-50 border-amber-200',
  },
  {
    icon: <Users className="h-6 w-6" />,
    titleKey: 'seminar.feature4_title',
    descKey: 'seminar.feature4_desc',
    color: 'text-violet-500',
    bg: 'bg-violet-50 border-violet-200',
  },
  {
    icon: <Laptop className="h-6 w-6" />,
    titleKey: 'seminar.feature5_title',
    descKey: 'seminar.feature5_desc',
    color: 'text-teal-500',
    bg: 'bg-teal-50 border-teal-200',
  },
  {
    icon: <Building2 className="h-6 w-6" />,
    titleKey: 'seminar.feature6_title',
    descKey: 'seminar.feature6_desc',
    color: 'text-rose-500',
    bg: 'bg-rose-50 border-rose-200',
  },
];

const FLOW = [
  'seminar.flow1',
  'seminar.flow2',
  'seminar.flow3',
  'seminar.flow4',
  'seminar.flow5',
];

const SeminarPage = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('seminar.page_title')}</title>
        <meta name="description" content={t('seminar.meta_description')} />
      </Helmet>

      {/* ヒーロー */}
      <section className="relative min-h-[42vh] flex flex-col justify-center overflow-hidden bg-[#0a0f1e] px-4 py-14">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/40 via-[#0a0f1e] to-teal-900/30 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(ellipse at 30% 50%, rgba(16,185,129,0.3) 0%, transparent 60%),
              radial-gradient(ellipse at 70% 30%, rgba(6,182,212,0.2) 0%, transparent 55%)`,
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white/80 text-sm mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            {t('seminar.back_home')}
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-4xl md:text-6xl font-black mb-4 leading-tight"
            style={{
              background: 'linear-gradient(135deg, #10b981, #06b6d4)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('seminar.hero_title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.6 }}
            className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed mb-6"
          >
            {t('seminar.hero_subtitle')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap justify-center gap-2"
          >
            {['seminar.tag1', 'seminar.tag2', 'seminar.tag3', 'seminar.tag4'].map((key) => (
              <span
                key={key}
                className="px-4 py-1.5 rounded-full text-sm font-semibold border"
                style={{ backgroundColor: 'rgba(16,185,129,0.12)', borderColor: 'rgba(16,185,129,0.4)', color: '#10b981' }}
              >
                {t(key)}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 概要 */}
      <section className="py-10 px-4 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
            className="text-xl md:text-2xl font-bold text-gray-900 mb-3"
          >
            {t('seminar.overview_title')}
          </motion.h2>
          <div className="w-10 h-1 bg-emerald-500 mx-auto mb-5 rounded-full" />
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
            className="text-gray-600 leading-relaxed text-sm md:text-base"
          >
            {t('seminar.overview_body')}
          </motion.p>
        </div>
      </section>

      {/* ハンズオン強調バナー */}
      <section className="py-8 px-4 bg-gradient-to-r from-teal-600 to-emerald-600 text-white">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
          className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-4 text-center md:text-left"
        >
          <Laptop className="h-12 w-12 flex-shrink-0 text-white/90" />
          <div>
            <h3 className="text-lg md:text-xl font-bold mb-1">{t('seminar.handson_title')}</h3>
            <p className="text-white/85 text-sm md:text-base leading-relaxed">{t('seminar.handson_body')}</p>
          </div>
        </motion.div>
      </section>

      {/* 特徴 */}
      <section className="py-10 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
            className="text-xl md:text-2xl font-bold text-center text-gray-900 mb-2"
          >
            {t('seminar.features_title_6')}
          </motion.h2>
          <div className="w-10 h-1 bg-emerald-500 mx-auto mb-7 rounded-full" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.titleKey}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i + 1}
                className={`rounded-xl border p-5 ${f.bg}`}
              >
                <div className={`mb-2 ${f.color}`}>{f.icon}</div>
                <h3 className="font-bold text-gray-900 text-base mb-1">{t(f.titleKey)}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{t(f.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 対応レベル + フロー 横並び */}
      <section className="py-10 px-4 bg-white">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">

          {/* 対応レベル */}
          <div>
            <motion.h2
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
              className="text-xl font-bold text-gray-900 mb-2"
            >
              {t('seminar.levels_title')}
            </motion.h2>
            <div className="w-8 h-1 bg-emerald-500 mb-5 rounded-full" />
            <div className="space-y-3">
              {(['seminar.level1', 'seminar.level2', 'seminar.level3'] as const).map((key, i) => (
                <motion.div
                  key={key}
                  variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i + 1}
                  className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100"
                >
                  <p className="text-gray-800 text-sm font-medium whitespace-pre-line">{t(key)}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* 開催フロー */}
          <div>
            <motion.h2
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
              className="text-xl font-bold text-gray-900 mb-2"
            >
              {t('seminar.flow_title')}
            </motion.h2>
            <div className="w-8 h-1 bg-emerald-500 mb-5 rounded-full" />
            <div className="space-y-3">
              {FLOW.map((key, i) => (
                <motion.div
                  key={key}
                  variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i + 1}
                  className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100"
                >
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-xs">
                    {i + 1}
                  </span>
                  <p className="text-gray-700 text-sm leading-relaxed pt-0.5">{t(key)}</p>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* CTA */}
      <section className="py-14 px-4 bg-gradient-to-br from-emerald-900 to-teal-900 text-white text-center">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
          className="max-w-xl mx-auto"
        >
          <Mail className="h-10 w-10 mx-auto mb-3 text-emerald-300" />
          <h2 className="text-xl md:text-2xl font-bold mb-3">{t('seminar.cta_title')}</h2>
          <p className="text-white/75 mb-6 leading-relaxed text-sm md:text-base">{t('seminar.cta_body')}</p>
          <Link
            to="/"
            onClick={(e) => { e.preventDefault(); window.location.href = '/#contact'; }}
            className="inline-block bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-8 py-3 rounded-full transition-colors shadow-lg"
          >
            {t('seminar.cta_button')}
          </Link>
        </motion.div>
      </section>
    </>
  );
};

export default SeminarPage;
